import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, ViewChild, inject } from '@angular/core';
import { ShelfWithProducts } from 'package-types/devices';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Product } from '../../models/Product';
import { Fridge } from '../../models/Fridge';
import { SectionWithStock } from '../../models/SectionWithStock';
import Utils from '../../utils';
import { Api } from '../../services/api/api.service';
import { NotificationService } from '../../services/notification/notification.service';
import { ProductService } from '../../services/product/product.service';
import { FridgeService } from '../../services/fridge/fridge.service';

import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import { UserService } from '../../services/user/user.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-change-amount-modal',
  templateUrl: './change-amount-modal.component.html',
  styleUrls: ['./change-amount-modal.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeAmountModalComponent {
  private modalController = inject(ModalController);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  fridgeService = inject(FridgeService);
  userService = inject(UserService);
  productService = inject(ProductService);
  private ngZone = inject(NgZone);
  private cdRef = inject(ChangeDetectorRef);
  i18n = inject(TranslateService);

  @Input('productId') productId: string;
  @Input('productIndex') productIndexInput: number | undefined;
  @Input('shelf') shelf: ShelfWithProducts;
  @Input('section') section: SectionWithStock;
  @Input('image') image: string;
  @Input('fridge') fridge: Fridge;
  @Input('rackNumber') rackNumber: number;
  @Input('openChange') openChange: boolean;

  @ViewChild('swiperEl') swiperEl: ElementRef;

  qty = 1;
  predictedAvailability;
  showReplace = false;
  product: Product;
  activeIndex = 0;
  sectionProducts: Product[] = [];
  bestBeforeDate: string | null = null;
  hasSavedBbd = false;
  readonly today = new Date().toISOString().split('T')[0];

  async back() {
    await this.modalController.dismiss();
  }

  async ionViewWillEnter() {
    this.product = this.productService.getProductFromStore(this.productId);
    const products: Product[] = [];
    // Use explicit productIndex if provided (handles duplicate productIds in section)
    this.activeIndex = this.productIndexInput !== undefined
      // @ts-ignore
      ? this.productIndexInput
      // @ts-ignore
      : this.section.productIds.findIndex((p) => p === this.productId);

    for (const product of this.section.productIds) {
      // @ts-ignore
      products.push(this.productService.getProductFromStore(product));
    }
    this.sectionProducts = products;
    if (this.openChange) {
      await this.replace();
      return;
    }
    // Swiper custom elements ignore [attr.initial-slide] after init — slide programmatically
    if (this.activeIndex > 0) {
      setTimeout(() => this.swiperEl?.nativeElement?.swiper?.slideTo(this.activeIndex, 0));
    }
    this.qty = this.section.products[this.activeIndex]?.quantity || 0;
    this.predictedAvailability = Utils.getPredictedAvailability(
      this.section.products?.[this.activeIndex]?.quantity,
      this.section.stock?.[this.activeIndex]?.predictedAvailability,
      this.fridgeService.activeFridge?.stockAnalysisPeriod || 168,
    );
    this.bestBeforeDate =
      (this.section.productsSettings?.find((s) => s.productId === this.productId)?.bestBeforeDate as string) ?? null;
    this.hasSavedBbd = !!this.bestBeforeDate;
  }

  plus() {
    this.qty += 1;
  }

  minus() {
    if (this.qty > 0) this.qty -= 1;
  }

  async changeQuantity() {
    const { section } = await Api.changeQuantity(
      this.section._id,
      this.product._id,
      this.qty,
      this.activeIndex,
    );
    await this.productService.replaceSection(section);
    await this.back();
    await this.notificationService.notify(this.i18n.instant('notification.success.change'));
  }

  async replace() {
    this.showReplace = true;
  }
  async remove() {
    const { success } = await ConfirmationModalComponent.show(
      this.modalController,
      this.i18n.instant('systemModal.modals.d_title'),
      '',
    );
    if (!success) return;
    const { section } = await Api.removeSectionProduct(this.section._id, this.activeIndex);
    // Update local state immediately before dismiss — OnPush prevents ExpressionChangedError
    await this.productService.replaceSection(section);
    this.productService.resetPagination();
    await this.modalController.dismiss();
  }
  async edit() {
    await this.modalController.dismiss({ action: 'edit', product: this.product });
  }
  async goBack() {
    this.openChange ? await this.back() : (this.showReplace = false);
  }

  async saveBestBeforeDate() {
    if (!this.bestBeforeDate) return;
    const { section } = await Api.setSectionProductSettings(this.section._id, this.productId, this.bestBeforeDate);
    this.section = { ...this.section, productsSettings: (section as any).productsSettings };
    this.productService.replaceSection(this.section);
    this.hasSavedBbd = true;
    await this.notificationService.notify(this.i18n.instant('notification.success.changed'));
    this.cdRef.markForCheck();
  }

  async clearBestBeforeDate() {
    const { section } = await Api.deleteSectionProductSettings(this.section._id, this.productId);
    this.section = { ...this.section, productsSettings: (section as any).productsSettings };
    this.bestBeforeDate = null;
    this.hasSavedBbd = false;
    this.productService.replaceSection(this.section);
    await this.notificationService.notify(this.i18n.instant('notification.success.changed'));
    this.cdRef.markForCheck();
  }

  onSlideChange(index) {
    this.ngZone.run(() => {
      this.activeIndex = index;
      this.qty = this.section.products[this.activeIndex]?.quantity || 0;
      this.product = this.sectionProducts[index];
      this.productId = this.section.productIds[index];
      this.bestBeforeDate =
        (this.section.productsSettings?.find((s) => s.productId === this.productId)?.bestBeforeDate as string) ?? null;
      this.hasSavedBbd = !!this.bestBeforeDate;
    });
  }

  get activeProductStock() {
    return [];
    //if (!this.product && !this.section.stock) return;
  }

  async copyText(val: any) {
    if (!val) return;
    await navigator.clipboard.writeText(val);
    await this.notificationService.notify(this.i18n.instant('notification.success.copy'));
  }
}
